"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { prefersReducedMotion, registerGsap } from "@/lib/motion/gsap";
import { acquireLenis, releaseLenis } from "@/lib/motion/smooth-scroll";
import { initChrome } from "@/lib/motion/chrome";
import { initField } from "@/lib/motion/field";
import { initSpotlight } from "@/lib/motion/spotlight";
import { initPage } from "@/lib/motion/page";
import { initFooterMotion, initFooterTools } from "@/lib/motion/footer";

/**
 * Site-wide motion that belongs to the shared chrome: smooth scroll, navbar
 * progress/scrolled state, gutter-rail canvas, panel spotlight, the inner pages'
 * chapter motion, footer reveal + parallax, footer tools. Renders nothing.
 * Re-initialises per route so ScrollTrigger order stays correct
 * (triggers below a page's pinned sections must be created after the pin).
 */
export function SiteMotion() {
  const pathname = usePathname();

  /* Smooth scroll: once for the whole session */
  useEffect(() => {
    if (prefersReducedMotion()) return;
    acquireLenis();
    return () => releaseLenis();
  }, []);

  /* Gutter rails + cursor spotlight: once for the whole session */
  useEffect(() => {
    const field = initField(prefersReducedMotion());
    const spot = initSpotlight();
    return () => {
      spot();
      field?.destroy();
    };
  }, []);

  /* Per-route chrome */
  useEffect(() => {
    const reduced = prefersReducedMotion();
    const cleanups: Array<() => void> = [];

    cleanups.push(initChrome());
    cleanups.push(initFooterTools(reduced));
    if (pathname !== "/") cleanups.push(initPage());
    if (!reduced) cleanups.push(initFooterMotion());

    let raf = 0;
    if (!reduced) {
      const { ScrollTrigger } = registerGsap();
      raf = window.requestAnimationFrame(() => ScrollTrigger.refresh());
    }

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      cleanups.reverse().forEach((fn) => fn());
    };
  }, [pathname]);

  return null;
}
